import { useEffect, useMemo, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, Loader2, Lock, RotateCcw, Save, SlidersHorizontal } from "lucide-react";

const ROLE_LABELS = { tag_head: "TAG Head", tag_lead: "TAG Lead", ta_lead: "TA Lead", ta_recruiter: "TA Recruiter" };
const SCOPE_LABELS = { own: "Own jobs", team: "Their team", all: "All jobs" };
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function Cell({ on, onClick, testid }) {
  return (
    <td className="px-2 py-2 text-center">
      <button type="button" onClick={onClick} data-testid={testid} aria-pressed={on}
        className={`w-6 h-6 rounded-md border inline-grid place-items-center transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-300 hover:border-indigo-400"}`}>
        {on && <Check className="w-3.5 h-3.5" />}
      </button>
    </td>
  );
}

function LockedCell() {
  return <td className="px-2 py-2 text-center"><span className="w-6 h-6 rounded-md inline-grid place-items-center bg-amber-50 border border-amber-200 text-amber-600"><Check className="w-3.5 h-3.5" /></span></td>;
}

function ScopeRow({ roles, matrix, setScope }) {
  return (
    <tr className="bg-indigo-50/40" data-testid="perm-row-scope">
      <td className="px-3 py-2 text-sm font-medium text-slate-800 min-w-[220px]">Which jobs and candidates they can see</td>
      {roles.map((r) => (
        <td key={r} className="px-2 py-2 text-center">
          <select data-testid={`perm-scope-${r}`} value={matrix[r].scope} onChange={(e) => setScope(r, e.target.value)}
            className="text-xs px-1.5 py-1 rounded-md bg-white border border-slate-300">
            {Object.entries(SCOPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </td>
      ))}
      <td className="px-2 py-2 text-center text-xs font-medium text-amber-700">All</td>
    </tr>
  );
}

export default function PermissionsMatrix() {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = (d) => { setData(d); setMatrix(d.matrix); };
  useEffect(() => { api.get("/tenant/permissions").then((r) => apply(r.data)).catch(() => toast.error("Couldn't load permissions")); }, []);

  const groups = useMemo(() => {
    const g = {};
    (data?.permissions || []).forEach((p) => { (g[p.group] = g[p.group] || []).push(p); });
    return Object.entries(g);
  }, [data]);

  if (!data || !matrix) return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading permissions…</div>;
  const roles = data.roles;
  const dirty = !same(matrix, data.matrix);
  const isDefault = same(matrix, data.defaults);

  const toggle = (r, key) => setMatrix((m) => {
    const has = m[r].perms.includes(key);
    return { ...m, [r]: { ...m[r], perms: has ? m[r].perms.filter((k) => k !== key) : [...m[r].perms, key] } };
  });
  const setScope = (r, scope) => setMatrix((m) => ({ ...m, [r]: { ...m[r], scope } }));
  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data: d } = await fn(); apply(d); await refreshUser?.(); toast.success(msg); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = () => run(() => api.put("/tenant/permissions", { matrix }), "Permissions saved");
  const reset = () => { if (window.confirm("Reset every role to the default permissions?")) run(() => api.post("/tenant/permissions/reset"), "Permissions reset to defaults"); };

  return (
    <div className="tp-card p-6" data-testid="permissions-matrix">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-display text-lg font-semibold flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-indigo-600" /> Role Permissions</div>
          <div className="text-xs text-slate-500 mt-0.5 max-w-xl">Choose what each role can do in this workspace. Changes apply to everyone with that role within about 30 seconds.</div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="perm-reset" onClick={reset} disabled={busy || isDefault}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"><RotateCcw className="w-3.5 h-3.5" /> Reset to defaults</button>
          <button type="button" data-testid="perm-save" onClick={save} disabled={busy || !dirty}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-40">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save changes</button>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2">
        <table className="w-full min-w-[640px] text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Permission</th>
              {roles.map((r) => <th key={r} className="px-2 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold text-center w-[104px]">{ROLE_LABELS[r]}</th>)}
              <th className="px-2 py-2 text-[11px] uppercase tracking-wider text-amber-700 font-semibold text-center w-[104px]"><span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Tenant Admin</span></th>
            </tr>
          </thead>
          <tbody>
            <ScopeRow roles={roles} matrix={matrix} setScope={setScope} />
            {groups.map(([g, perms]) => [
              <tr key={g}><td colSpan={roles.length + 2} className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-wider text-indigo-600 font-semibold">{g}</td></tr>,
              ...perms.map((p) => (
                <tr key={p.key} className="border-b border-slate-100" data-testid={`perm-row-${p.key}`}>
                  <td className="px-3 py-2 text-sm text-slate-700 min-w-[220px]">{p.label}</td>
                  {roles.map((r) => <Cell key={r} on={matrix[r].perms.includes(p.key)} onClick={() => toggle(r, p.key)} testid={`perm-${p.key}-${r}`} />)}
                  <LockedCell />
                </tr>
              )),
            ])}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="perm-admin-only">
        <div className="font-semibold mb-1 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Always Tenant Admin only</div>
        {data.admin_only.join(" · ")}
      </div>
    </div>
  );
}
